import { MAX_INDENT, MIN_INDENT, normalizeIndent } from "./formattingConfig.mjs";

const ALIGNABLE_NODES = new Set(["paragraph", "heading", "blockquote"]);
const STRUCTURAL_ANCESTORS = new Set(["taskList", "bulletList", "orderedList", "blockquote"]);
const STRUCTURAL_LEAVES = new Set(["paragraph", "heading", "codeBlock", "horizontalRule"]);
const STATE_CACHE = new WeakMap();

function stateFromCoverage(matches, total) {
  if (total === 0 || matches === 0) return false;
  return matches === total ? true : "mixed";
}

function attrsMatch(actual, expected) {
  return Object.entries(expected).every(([name, value]) => actual[name] === value);
}

function itemTypeAtPosition(doc, position) {
  const resolved = doc.resolve(Math.min(position + 1, doc.content.size));
  for (let depth = resolved.depth; depth > 0; depth -= 1) {
    const name = resolved.node(depth).type.name;
    if (name === "taskItem" || name === "listItem") return name;
  }
  return null;
}

function structuralUnit(doc, node, position, parent) {
  if (!STRUCTURAL_LEAVES.has(node.type.name)) return null;
  const structures = [{ type: node.type.name, attrs: node.attrs }];
  if (parent?.type.name === "doc") return { key: `${node.type.name}:${position}`, structures };
  const resolved = doc.resolve(Math.min(position + 1, doc.content.size));
  for (let depth = resolved.depth; depth > 0; depth -= 1) {
    const ancestor = resolved.node(depth);
    if (STRUCTURAL_ANCESTORS.has(ancestor.type.name)) {
      structures.push({ type: ancestor.type.name, attrs: ancestor.attrs });
    }
  }
  return { key: `${node.type.name}:${position}`, structures };
}

function addMarkCoverage(markCoverage, marks, selected) {
  for (const mark of marks) {
    let record = markCoverage.get(mark.type.name);
    if (!record) {
      record = { coverage: 0, variants: new Map() };
      markCoverage.set(mark.type.name, record);
    }
    record.coverage += selected;
    const entries = Object.entries(mark.attrs).sort(([left], [right]) => left.localeCompare(right));
    const key = JSON.stringify(entries);
    const variant = record.variants.get(key);
    if (variant) variant.coverage += selected;
    else record.variants.set(key, { attrs: mark.attrs, coverage: selected });
  }
}

function hasMarkAttribute(markCoverage, markName, attributeName) {
  const record = markCoverage.get(markName);
  if (!record) return false;
  for (const variant of record.variants.values()) {
    if (variant.coverage > 0 && variant.attrs[attributeName] != null) return true;
  }
  return false;
}

function analyzeNonEmptySelection(state) {
  const { doc, selection } = state;
  const markCoverage = new Map();
  const structural = new Map();
  const alignmentCounts = new Map();
  const itemTypes = new Set();
  const ordinaryIndents = [];
  let textTotal = 0;
  let alignableTotal = 0;

  doc.nodesBetween(selection.from, selection.to, (node, position, parent) => {
    if (node.isText) {
      const selected = Math.max(0,
        Math.min(selection.to, position + node.nodeSize) - Math.max(selection.from, position));
      if (selected > 0) {
        textTotal += selected;
        addMarkCoverage(markCoverage, node.marks, selected);
      }
    }

    const unit = structuralUnit(doc, node, position, parent);
    if (unit) structural.set(unit.key, unit);

    const nodeName = node.type.name;
    if (nodeName === "taskItem" || nodeName === "listItem") itemTypes.add(nodeName);
    if (!ALIGNABLE_NODES.has(nodeName)) return;

    alignableTotal += 1;
    const alignment = node.attrs.textAlign;
    alignmentCounts.set(alignment, (alignmentCounts.get(alignment) ?? 0) + 1);
    const itemType = parent?.type.name === "doc" ? null : itemTypeAtPosition(doc, position);
    if (itemType) itemTypes.add(itemType);
    else ordinaryIndents.push(normalizeIndent(node.attrs.indent));
  });

  return { markCoverage, structural, alignmentCounts, itemTypes, ordinaryIndents, textTotal, alignableTotal };
}

function analyzeEmptySelection(state) {
  const { selection } = state;
  const itemTypes = new Set();
  const ordinaryIndents = [];
  const alignmentCounts = new Map();
  let alignableTotal = 0;
  for (let depth = selection.$from.depth; depth > 0; depth -= 1) {
    const node = selection.$from.node(depth);
    const nodeName = node.type.name;
    if (nodeName === "taskItem" || nodeName === "listItem") itemTypes.add(nodeName);
    else if (ALIGNABLE_NODES.has(nodeName)) {
      ordinaryIndents.push(normalizeIndent(node.attrs.indent));
      alignableTotal += 1;
      const alignment = node.attrs.textAlign;
      alignmentCounts.set(alignment, (alignmentCounts.get(alignment) ?? 0) + 1);
    }
  }
  if (itemTypes.size > 0) ordinaryIndents.length = 0;
  return {
    markCoverage: new Map(),
    structural: new Map(),
    alignmentCounts,
    itemTypes,
    ordinaryIndents,
    textTotal: 0,
    alignableTotal
  };
}

function createSnapshot(state) {
  const nonEmpty = state.selection.empty === false && state.selection.from < state.selection.to;
  const data = nonEmpty ? analyzeNonEmptySelection(state) : analyzeEmptySelection(state);
  const indentMixed = data.itemTypes.size > 1
    || (data.itemTypes.size > 0 && data.ordinaryIndents.length > 0);
  const listItemType = indentMixed || data.itemTypes.size !== 1
    ? null
    : data.itemTypes.values().next().value;

  return Object.freeze({
    nonEmpty,
    hasAlignable: data.alignableTotal > 0,
    hasColorFormatting: nonEmpty && data.textTotal > 0
      ? hasMarkAttribute(data.markCoverage, "textStyle", "color")
        || hasMarkAttribute(data.markCoverage, "highlight", "color")
      : undefined,
    indentMixed,
    listItemType,
    markState(markName, attrs = {}) {
      if (!nonEmpty || data.textTotal === 0) return undefined;
      const record = data.markCoverage.get(markName);
      if (!record) return false;
      const entries = Object.entries(attrs);
      if (entries.length === 0) return stateFromCoverage(record.coverage, data.textTotal);
      let matches = 0;
      for (const variant of record.variants.values()) {
        if (attrsMatch(variant.attrs, attrs)) matches += variant.coverage;
      }
      return stateFromCoverage(matches, data.textTotal);
    },
    linkState() {
      if (!nonEmpty || data.textTotal === 0) return undefined;
      const record = data.markCoverage.get("link");
      if (!record || record.coverage === 0) return false;
      const hrefs = new Set();
      for (const variant of record.variants.values()) hrefs.add(variant.attrs.href);
      if (record.coverage !== data.textTotal || hrefs.size !== 1) return "mixed";
      return true;
    },
    structuralState(nodeName, attrs = {}) {
      if (!nonEmpty || data.structural.size === 0) return undefined;
      let matches = 0;
      for (const unit of data.structural.values()) {
        if (unit.structures.some((record) => (
          record.type === nodeName && attrsMatch(record.attrs, attrs)
        ))) matches += 1;
      }
      return stateFromCoverage(matches, data.structural.size);
    },
    alignmentState(alignment) {
      if (!nonEmpty) return undefined;
      return stateFromCoverage(data.alignmentCounts.get(alignment) ?? 0, data.alignableTotal);
    },
    indentCanChange(increase) {
      if (indentMixed) return false;
      if (data.itemTypes.size === 1) return true;
      const limit = increase ? MAX_INDENT : MIN_INDENT;
      return data.ordinaryIndents.some((indent) => increase ? indent < limit : indent > limit);
    }
  });
}

export function getEditorSelectionAnalysis(editor) {
  try {
    const state = editor?.state;
    if (!state || typeof state !== "object" || typeof state.doc?.nodesBetween !== "function") return null;
    const cached = STATE_CACHE.get(state);
    if (cached) return cached;
    const snapshot = createSnapshot(state);
    STATE_CACHE.set(state, snapshot);
    return snapshot;
  } catch {
    return null;
  }
}
